import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Check, Plus, Star, X } from "lucide-react";
import { useReducedMotion, motion, AnimatePresence } from "motion/react";

import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import {
  MAX_TARGETS_PER_RULE,
  formatDurationMinutes,
  normalizeRuleWindow,
} from "../../lib/rules/ruleDerivation";
import type { Rule, Target } from "../../lib/rules/ruleTypes";
import { BottomSheet } from "../habits/BottomSheet";
import { TargetSelectRow } from "./RuleEditorSheet";
import {
  DEFAULT_QUICK_DURATION_MINUTES,
  SavedQuickSession,
  QuickSessionEntry,
  buildAutoSessionName,
  areTargetIdsAvailable,
  isSessionSaved,
  maxQuickDurationMinutes,
  pruneToAvailableTargetIds,
  sanitizeQuickDuration,
} from "./quickStartSession";
import {
  QUICK_START_FAB_ICONS,
  isQuickStartFabIconName,
  resolveQuickStartFabIcon,
} from "./quickStartIcons";

type HistoryTab = "recent" | "saved";

type QuickStartDrawerProps = {
  open: boolean;
  targets: Target[] | undefined;
  rules: Rule[] | undefined;
  fabIcon: string | undefined;
  recentSessions: QuickSessionEntry[];
  savedSessions: SavedQuickSession[];
  sessionActive: boolean;
  selectedTargetIds: string[];
  durationMinutes: number;
  onSelectedTargetIdsChange: (ids: string[]) => void;
  onDurationChange: (minutes: number) => void;
  onFabIconChange: (icon: string) => void;
  onSaveFromRecent: (targetIds: string[], durationMinutes: number, name: string) => void;
  onApplySession: (targetIds: string[], durationMinutes: number) => void;
  onCreateTarget: () => void;
  onClose: () => void;
};

export function QuickStartDrawer({
  open,
  targets,
  rules,
  fabIcon,
  recentSessions,
  savedSessions,
  sessionActive,
  selectedTargetIds,
  durationMinutes,
  onSelectedTargetIdsChange,
  onDurationChange,
  onFabIconChange,
  onSaveFromRecent,
  onApplySession,
  onCreateTarget,
  onClose,
}: QuickStartDrawerProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const [historyTab, setHistoryTab] = useState<HistoryTab>("recent");
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [savePrompt, setSavePrompt] = useState<{
    targetIds: string[];
    durationMinutes: number;
    name: string;
  } | null>(null);
  const [limitNotice, setLimitNotice] = useState(false);
  const currentTargets = targets ?? [];
  const targetsById = useMemo(() => new Map(currentTargets.map((target) => [target._id, target])), [currentTargets]);
  const availableRecentSessions = useMemo(
    () => recentSessions.filter((entry) => areTargetIdsAvailable(entry.targetIds, targetsById)),
    [recentSessions, targetsById],
  );
  const availableSavedSessions = useMemo(
    () => savedSessions.filter((entry) => areTargetIdsAvailable(entry.targetIds, targetsById)),
    [savedSessions, targetsById],
  );
  const selected = new Set(selectedTargetIds);
  const validSelectedTargetIds = useMemo(
    () => pruneToAvailableTargetIds(selectedTargetIds, targetsById),
    [selectedTargetIds, targetsById],
  );
  const atLimit = selected.size >= MAX_TARGETS_PER_RULE;
  const maxDuration = maxQuickDurationMinutes();
  const safeDuration = sanitizeQuickDuration(durationMinutes);
  const FabIcon = resolveQuickStartFabIcon(fabIcon);

  const toggleTarget = (id: string) => {
    if (selected.has(id)) {
      onSelectedTargetIdsChange(selectedTargetIds.filter((targetId) => targetId !== id));
      return;
    }
    if (atLimit) {
      setLimitNotice(true);
      return;
    }
    onSelectedTargetIdsChange([...selectedTargetIds, id]);
  };

  const applySessionPreset = useCallback(
    (targetIds: string[], nextDuration: number) => {
      onSelectedTargetIdsChange(targetIds);
      onDurationChange(sanitizeQuickDuration(nextDuration));
    },
    [onDurationChange, onSelectedTargetIdsChange],
  );

  useEffect(() => {
    if (!open) {
      setIconPickerOpen(false);
      setSavePrompt(null);
      setLimitNotice(false);
    }
  }, [open]);

  useEffect(() => {
    if (!atLimit && limitNotice) setLimitNotice(false);
  }, [atLimit, limitNotice]);

  useEffect(() => {
    const pruned = pruneToAvailableTargetIds(selectedTargetIds, targetsById);
    if (pruned.length !== selectedTargetIds.length) {
      onSelectedTargetIdsChange(pruned);
    }
  }, [onSelectedTargetIdsChange, selectedTargetIds, targetsById]);

  const openSavePrompt = (entry: QuickSessionEntry) => {
    setSavePrompt({
      targetIds: entry.targetIds,
      durationMinutes: entry.durationMinutes,
      name: entry.autoName,
    });
  };

  const confirmSave = () => {
    if (!savePrompt) return;
    onSaveFromRecent(savePrompt.targetIds, savePrompt.durationMinutes, savePrompt.name);
    setSavePrompt(null);
  };

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="quick-start-title" maxHeight="80svh">
      <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-1">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="liquid-glass-icon-ring grid h-10 w-10 shrink-0 place-items-center"
            aria-label={t.rules.quickStart.changeIcon}
            onClick={() => setIconPickerOpen((value) => !value)}
            onContextMenu={(event) => {
              event.preventDefault();
              setIconPickerOpen(true);
            }}
          >
            <FabIcon size={18} aria-hidden />
          </button>
          <div className="min-w-0">
            <h2 id="quick-start-title" className="truncate text-lg font-semibold text-[oklch(92%_0.02_150)]">
              {t.rules.quickStart.drawerTitle}
            </h2>
            {sessionActive ? (
              <p className="truncate text-xs text-[oklch(72%_0.09_155)]">{t.rules.quickStart.sessionActive}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="icon-button"
          style={{ minWidth: 36, minHeight: 36, border: "none" }}
          aria-label={t.common.close}
        >
          <X size={18} />
        </button>
      </div>

      {iconPickerOpen ? (
        <div className="px-4 pb-2">
          <p className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
            {t.rules.quickStart.changeIcon}
          </p>
          <div className="liquid-glass-inset grid grid-cols-6 gap-1.5 p-2">
            {QUICK_START_FAB_ICONS.map(({ name, Icon }) => {
              const active = fabIcon === name || (!fabIcon && name === "Zap");
              return (
                <button
                  key={name}
                  type="button"
                  aria-label={name}
                  aria-pressed={active}
                  className={`grid min-h-11 place-items-center rounded-lg transition-colors ${active ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]" : "text-[oklch(74%_0.025_155)] active:bg-[oklch(20%_0.02_165)]"}`}
                  onClick={() => {
                    if (isQuickStartFabIconName(name)) onFabIconChange(name);
                    setIconPickerOpen(false);
                  }}
                >
                  <Icon size={18} aria-hidden />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
        <section className="pb-4">
          <div className="flex items-baseline justify-between pb-1.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
              {t.rules.targetsSection}
            </h3>
            <span className="text-[11px] font-semibold text-[oklch(58%_0.03_155)]">
              {formatTemplate(t.rules.targetsLimitHint, { max: MAX_TARGETS_PER_RULE })}
              {" · "}
              {selected.size}/{MAX_TARGETS_PER_RULE}
            </span>
          </div>

          {targets === undefined ? (
            <div className="liquid-glass-inset grid gap-2 p-3">
              <span className="h-10 rounded-lg bg-[oklch(23%_0.018_165)]" />
              <span className="h-10 rounded-lg bg-[oklch(23%_0.018_165)]" />
            </div>
          ) : currentTargets.length === 0 ? (
            <button
              type="button"
              onClick={onCreateTarget}
              className="liquid-glass-dashed-cta flex w-full items-center gap-3 px-3 py-4 text-left"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]">
                <Plus size={18} />
              </span>
              <span>
                <span className="block text-sm font-semibold text-[oklch(90%_0.02_150)]">
                  {t.rules.quickStart.createTarget}
                </span>
                <span className="block text-xs text-[oklch(64%_0.025_155)]">{t.rules.createTargetHint}</span>
              </span>
            </button>
          ) : (
            <div className="liquid-glass-inset grid gap-1 p-1">
              {currentTargets.map((target) => (
                <TargetSelectRow
                  key={target._id}
                  target={target}
                  rules={rules}
                  targetsById={targetsById}
                  checked={selected.has(target._id)}
                  disabled={!selected.has(target._id) && atLimit}
                  onToggle={() => toggleTarget(target._id)}
                />
              ))}
              <button
                type="button"
                onClick={onCreateTarget}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-sm font-semibold text-[oklch(72%_0.09_155)] transition-colors active:bg-[oklch(20%_0.02_165)]"
              >
                <span className="liquid-glass-icon-ring grid h-7 w-7 shrink-0 place-items-center">
                  <Plus size={14} />
                </span>
                {t.rules.quickStart.createTarget}
              </button>
            </div>
          )}
          {limitNotice ? (
            <p className="pt-2 text-xs text-[oklch(72%_0.10_75)]" role="status">
              {formatTemplate(t.rules.targetsLimitReached, { max: MAX_TARGETS_PER_RULE })}
            </p>
          ) : null}
        </section>

        <section className="pb-4">
          <div className="liquid-glass-inset grid gap-1.5 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[oklch(64%_0.025_155)]">{t.rules.quickStart.durationLabel}</span>
              <span className="mono font-semibold text-[oklch(86%_0.03_155)]">
                {formatDurationMinutes(safeDuration, languageCode)}
              </span>
            </div>
            <input
              type="range"
              min={Math.min(5, maxDuration)}
              max={maxDuration}
              step={5}
              value={safeDuration}
              onChange={(event) =>
                onDurationChange(
                  normalizeRuleWindow({
                    startMinute: 0,
                    durationMinutes: Number(event.target.value),
                  }).durationMinutes,
                )
              }
              className="w-full accent-[oklch(72%_0.09_155)]"
              aria-label={t.rules.quickStart.durationAria}
            />
          </div>
        </section>

        <section className="pb-2">
          <HistoryToggle tab={historyTab} onChange={setHistoryTab} t={t} />
          <div className="liquid-glass-inset mt-2 grid gap-1 p-1">
            {historyTab === "recent" ? (
              availableRecentSessions.length === 0 ? (
                <p className="px-2 py-3 text-sm text-[oklch(64%_0.025_155)]">{t.rules.quickStart.noRecents}</p>
              ) : (
                availableRecentSessions.map((entry) => {
                  const saved = isSessionSaved(savedSessions, entry.targetIds, entry.durationMinutes);
                  return (
                    <HistoryRow
                      key={`${entry.lastUsedAt}-${sessionFingerprint(entry)}`}
                      title={entry.autoName}
                      subtitle={formatDurationMinutes(entry.durationMinutes, languageCode)}
                      onSelect={() => applySessionPreset(entry.targetIds, entry.durationMinutes)}
                      trailing={
                        <button
                          type="button"
                          className="grid min-h-11 min-w-11 place-items-center rounded-lg text-[oklch(78%_0.08_85)] transition-colors active:bg-[oklch(20%_0.02_165)] disabled:opacity-40"
                          aria-label={
                            saved ? t.rules.quickStart.savedStarAria : t.rules.quickStart.saveStarAria
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            if (saved) return;
                            openSavePrompt(entry);
                          }}
                        >
                          <Star size={16} fill={saved ? "currentColor" : "none"} aria-hidden />
                        </button>
                      }
                    />
                  );
                })
              )
            ) : availableSavedSessions.length === 0 ? (
              <p className="px-2 py-3 text-sm text-[oklch(64%_0.025_155)]">{t.rules.quickStart.noSaved}</p>
            ) : (
              availableSavedSessions.map((entry) => (
                <HistoryRow
                  key={`${entry.savedAt}-${sessionFingerprint(entry)}`}
                  title={entry.name}
                  subtitle={formatDurationMinutes(entry.durationMinutes, languageCode)}
                  onSelect={() => applySessionPreset(entry.targetIds, entry.durationMinutes)}
                  trailing={
                    <span className="grid min-h-11 min-w-11 place-items-center text-[oklch(78%_0.08_85)]">
                      <Star size={16} fill="currentColor" aria-hidden />
                    </span>
                  }
                />
              ))
            )}
          </div>
        </section>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        {savePrompt ? (
          <motion.div
            key="quick-start-save-footer"
            className="liquid-glass-sheet-footer grid gap-2 px-4 pt-3"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={prefersReducedMotion ? { duration: 0.12 } : { type: "spring", stiffness: 420, damping: 36 }}
          >
            <div>
              <p className="text-sm font-semibold text-[oklch(88%_0.02_150)]">{t.rules.quickStart.savePromptTitle}</p>
              <p className="text-xs text-[oklch(64%_0.025_155)]">{t.rules.quickStart.savePromptHint}</p>
            </div>
            <p className="text-sm font-medium text-[oklch(82%_0.02_150)]">{t.rules.quickStart.savePrompt}</p>
            <input
              type="text"
              value={savePrompt.name}
              onChange={(event) => setSavePrompt({ ...savePrompt, name: event.target.value })}
              placeholder={t.rules.quickStart.saveNamePlaceholder}
              className="liquid-glass-input min-h-11 rounded-lg px-3 text-base text-[oklch(90%_0.02_150)]"
              aria-label={t.rules.quickStart.saveNamePlaceholder}
            />
            <div className="flex gap-2">
              <button type="button" className="secondary-action flex-1" onClick={() => setSavePrompt(null)}>
                {t.common.cancel}
              </button>
              <button
                type="button"
                className="primary-action flex-1"
                disabled={!savePrompt.name.trim()}
                onClick={confirmSave}
              >
                {t.common.save}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="quick-start-start-footer"
            className="liquid-glass-sheet-footer px-4 pt-3"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={prefersReducedMotion ? { duration: 0.12 } : { type: "spring", stiffness: 420, damping: 36 }}
          >
            <button
              type="button"
              className="primary-action w-full"
              disabled={validSelectedTargetIds.length === 0}
              onClick={() => onApplySession(validSelectedTargetIds, safeDuration)}
            >
              {t.rules.quickStart.startButton}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </BottomSheet>
  );
}

function sessionFingerprint(entry: { targetIds: string[]; durationMinutes: number }) {
  return [...entry.targetIds].sort().join(",") + ":" + entry.durationMinutes;
}

function HistoryToggle({
  tab,
  onChange,
  t,
}: {
  tab: HistoryTab;
  onChange: (tab: HistoryTab) => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const prefersReducedMotion = useReducedMotion();
  const options: ReadonlyArray<{ value: HistoryTab; label: string }> = [
    { value: "recent", label: t.rules.quickStart.recentTab },
    { value: "saved", label: t.rules.quickStart.savedTab },
  ];

  return (
    <div className="liquid-glass-segmented">
      {options.map((option) => {
        const active = tab === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            className={`relative flex-1 rounded-md px-2.5 py-2 text-xs font-semibold transition-colors ${active ? "text-[oklch(12%_0.015_160)]" : "text-[oklch(70%_0.025_155)]"}`}
          >
            {active ? (
              <motion.span
                layoutId="quick-start-history-tab"
                className="absolute inset-0 rounded-md bg-[oklch(72%_0.09_155)]"
                transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 38 }}
              />
            ) : null}
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function HistoryRow({
  title,
  subtitle,
  onSelect,
  trailing,
}: {
  title: string;
  subtitle: string;
  onSelect: () => void;
  trailing: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1 rounded-lg">
      <button
        type="button"
        onClick={onSelect}
        className="flex min-h-11 min-w-0 flex-1 items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition-colors active:bg-[oklch(20%_0.02_165)]"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-[oklch(90%_0.02_150)]">
            {title}
          </span>
          <span className="block text-xs text-[oklch(64%_0.025_155)]">{subtitle}</span>
        </span>
      </button>
      {trailing}
    </div>
  );
}

export { buildAutoSessionName, DEFAULT_QUICK_DURATION_MINUTES };
